import { useQuery, useMutation, useQueryClient, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type { PendingEnrollmentResponse } from '../generated/sportSchool';
import type {
  EnrollmentResponse,
  EnrollmentListResponse,
  EnrollStudentRequest,
  TransferStudentGroupRequest,
  BulkTransferStudentGroupRequest,
  ScheduleEnrollmentResponse,
  EnrollStudentGroupRequest,
  TransferStudentGroupNextMonthRequest,
  CreateSubscriptionSuspensionRequest,
  UpdateSubscriptionSuspensionRequest,
  SubscriptionSuspensionResponse,
  UpdateSubscriptionEndDateRequest,
} from '@sportschool/shared-types';

export interface EnrollmentPriceCalculationResponse {
  basePriceSingle: number;
  equipPriceSingle: number;
  sessionCount: number;
  totalBasePrice: number;
  totalEquipPrice: number;
  totalPrice: number;
  billingMonthStr: string;
  billingMonth: string;
  isNextMonth: boolean;
  savingsPercent: number;
  makeUpDiscountAmount?: number;
  isMakeUpCompensatory?: boolean;
  firstSessionDate?: string;
  discountPercent?: number;
  discountAmount?: number;
  originalBasePriceSingle?: number;
  isCouponApplied?: boolean;
  isFirstSession?: boolean;
  overpaymentAmount?: number;
  overpaymentDeduction?: number;
  originalTotalPrice?: number;
  vouchersAmount?: number;
  vouchersDeduction?: number;
  nextMonthStr?: string | null;
  nextMonthTotalPrice?: number | string | null;
  isWaitlistRequired?: boolean;
  waitlistReason?: string | null;
}

export function useCalculateEnrollmentPrice(
  schoolId: string,
  params: {
    sessionId: string;
    studentMemberId: string;
    type: string;
    makeUpCreditId?: string;
    discountCouponId?: string;
    useGiftVouchers: boolean;
  },
  options?: { enabled?: boolean }
) {
  return useQuery<EnrollmentPriceCalculationResponse>({
    queryKey: ['enrollments', 'calculate-price', schoolId, params],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/enrollments/calculate-price`, {
        params: {
          sessionId: params.sessionId,
          studentMemberId: params.studentMemberId,
          type: params.type,
          makeUpCreditId: params.makeUpCreditId,
          discountCouponId: params.discountCouponId || undefined,
          useGiftVouchers: params.useGiftVouchers,
        },
      });
      return res.data;
    },
    enabled: !!schoolId && !!params.sessionId && !!params.studentMemberId && (options?.enabled ?? true),
  });
}

/** Query options of `useEnrollments` — shared with the offline prefetch so the cache keys cannot drift. */
export function enrollmentsQueryOptions(schoolId: string, sessionId?: string, upcomingOnly?: boolean) {
  return queryOptions({
    queryKey: ['enrollments', schoolId, sessionId, upcomingOnly],
    queryFn: async (): Promise<EnrollmentResponse[]> => {
      const url = sessionId
        ? `/schools/${schoolId}/enrollments/session/${sessionId}`
        : `/schools/${schoolId}/enrollments/student/me`;

      const params = upcomingOnly !== undefined ? { upcomingOnly } : {};
      const res = await api.get(url, { params });
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useEnrollments(schoolId: string, sessionId?: string, upcomingOnly?: boolean) {
  return useQuery(enrollmentsQueryOptions(schoolId, sessionId, upcomingOnly));
}

export function useAllEnrollments(schoolId: string, page: number = 1, pageSize: number = 10) {
  return useQuery<EnrollmentListResponse>({
    queryKey: ['enrollments', schoolId, 'all', page, pageSize],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/enrollments`, {
        params: { page, pageSize },
      });
      return res.data;
    },
    enabled: !!schoolId,
    placeholderData: keepPreviousData,
  });
}

export function useEnrollStudent(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: EnrollStudentRequest) => {
      const res = await api.post(`/schools/${schoolId}/enrollments`, data);
      return res.data;
    },
    onSuccess: (enrollment: EnrollmentResponse) => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: ['payments', schoolId] });
      qc.invalidateQueries({ queryKey: ['credits', schoolId] });
      qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes(`/schools/${schoolId}/credits`) });
      qc.invalidateQueries({ queryKey: ['coupons', schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/coupons`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/billing/bills`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      qc.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          (query.queryKey[0].includes(`/schools/${schoolId}/coupons`) ||
           query.queryKey[0].includes(`/schools/${schoolId}/balance`))
      });
      // An online-payment enrollment is registered only after the payment is confirmed
      if (!enrollment?.paymentId) {
        toast.success('Student enrolled');
      }
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to enroll student', code, args);
    },
  });
}

export function useCancelEnrollment(schoolId: string, enrollmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/schools/${schoolId}/enrollments/${enrollmentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['coupons', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/coupons`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Enrollment cancelled');
    },
    onError: () => toast.error('Failed to cancel enrollment'),
  });
}

export interface StudentEnrollmentFilters {
  locationId?: string;
  type?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  scope?: 'upcoming' | 'past' | 'all';
  sortDirection?: 'asc' | 'desc';
}

export function useStudentEnrollments(
  schoolId: string, 
  studentMemberId: string, 
  upcomingOnly = false,
  filters?: StudentEnrollmentFilters,
  options?: { enabled?: boolean }
) {
  return useQuery<EnrollmentListResponse>({
    queryKey: ['enrollments', schoolId, studentMemberId, upcomingOnly, filters?.locationId, filters?.type, filters?.status, filters?.page, filters?.pageSize, filters?.scope, filters?.sortDirection],
    queryFn: async () => {
      const params: Record<string, unknown> = { upcomingOnly };
      if (filters?.locationId) params.locationId = filters.locationId;
      if (filters?.type) params.type = filters.type;
      if (filters?.status) params.status = filters.status;
      if (filters?.page) params.page = filters.page;
      if (filters?.pageSize) params.pageSize = filters.pageSize;
      if (filters?.scope && filters.scope !== 'all') params.scope = filters.scope;
      if (filters?.sortDirection) params.sortDirection = filters.sortDirection;

      const res = await api.get(`/schools/${schoolId}/enrollments/student/${studentMemberId}`, { params });
      if (Array.isArray(res.data)) {
        return { items: res.data, totalCount: res.data.length };
      }
      return res.data;
    },
    enabled: (options?.enabled ?? true) && !!schoolId && !!studentMemberId,
    placeholderData: keepPreviousData,
  });
}

export interface EnrollMonthlyRequest {
  studentMemberId: string;
  startDateOption: 'NextMonth' | 'NearestSession' | 'SpecificSession' | 'CurrentMonth';
  specificSessionId?: string;
  isZeroPayment?: boolean;
}

export function useEnrollMonthly(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, data }: { scheduleId: string; data: EnrollMonthlyRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/schedule/${scheduleId}/monthly`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['pending-enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: ['schedules', schoolId] });
      qc.invalidateQueries({ queryKey: ['payments', schoolId] });
      qc.invalidateQueries({ queryKey: ['coupons', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/coupons`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/billing/bills`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      qc.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes(`/schools/${schoolId}/attendance`)
      });
      toast.success('Student zapisany pomyślnie na abonament');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się zapisać studenta na abonament', code, args);
    },
  });
}

export function useTransferStudentGroup(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, studentMemberId, data }: { scheduleId: string; studentMemberId: string; data: TransferStudentGroupRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/schedule/${scheduleId}/students/${studentMemberId}/transfer`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: ['members', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zawodnik został przeniesiony do nowej grupy');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się przenieść zawodnika', code, args);
    },
  });
}

export function useBulkTransferStudentGroup(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, data }: { scheduleId: string; data: BulkTransferStudentGroupRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/schedule/${scheduleId}/transfer-bulk`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: ['members', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zawodnicy zostali przeniesieni do nowej grupy');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się przenieść zawodników', code, args);
    },
  });
}

export function useUnenrollStudentGroup(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, studentMemberId }: { scheduleId: string; studentMemberId: string }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/schedule/${scheduleId}/students/${studentMemberId}/unenroll`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: ['members', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zawodnik został wypisany z grupy');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się wypisać zawodnika z grupy', code, args);
    },
  });
}

export function useCancelEnrollmentMutation(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      arg: string | {
        enrollmentId: string;
        issueFollowUp?: boolean;
        refundFollowUp?: boolean;
        hardDelete?: boolean;
        decline?: boolean;
      }
    ) => {
      const enrollmentId = typeof arg === 'string' ? arg : arg.enrollmentId;
      const issueFollowUp = typeof arg === 'string' ? false : (arg.issueFollowUp ?? false);
      const refundFollowUp = typeof arg === 'string' ? false : (arg.refundFollowUp ?? false);
      const hardDelete = typeof arg === 'string' ? false : (arg.hardDelete ?? false);
      const decline = typeof arg === 'string' ? false : (arg.decline ?? false);

      const params = new URLSearchParams();
      if (issueFollowUp) params.append('issueFollowUp', 'true');
      if (refundFollowUp) params.append('refundFollowUp', 'true');
      if (hardDelete) params.append('hardDelete', 'true');
      if (decline) params.append('decline', 'true');
      const queryString = params.toString() ? `?${params.toString()}` : '';
      await api.delete(`/schools/${schoolId}/enrollments/${enrollmentId}${queryString}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['pending-enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['credits', schoolId] });
      qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes(`/schools/${schoolId}/credits`) });
      qc.invalidateQueries({ queryKey: ['bills', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zapis został anulowany', 'toasts.enrollment_cancelled');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const code = data?.code || data?.Code;
      if (code) {
        // The enrollment state changed server-side (e.g. no longer pending) — refresh stale lists.
        qc.invalidateQueries({ queryKey: ['pending-enrollments', schoolId] });
        qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
        toast.error(data?.detail || data?.Detail || 'Nie udało się anulować zapisu', code);
        return;
      }
      toast.error('Nie udało się anulować zapisu', 'toasts.enrollment_cancel_failed');
    },
  });
}

export function useApproveEnrollmentMutation(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: string | { enrollmentId: string; allowOverbooking?: boolean }) => {
      const id = typeof args === 'string' ? args : args.enrollmentId;
      const overbooking = typeof args === 'object' ? !!args.allowOverbooking : false;
      await api.post(`/schools/${schoolId}/enrollments/${id}/approve${overbooking ? '?allowOverbooking=true' : ''}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['pending-enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zapis został zatwierdzony', 'toasts.enrollment_approved');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const code = data?.code || data?.Code;
      if (code === 'group_full_overbooking_required' || code === 'session_full_overbooking_required') {
        return;
      }
      toast.error('Nie udało się zatwierdzić zapisu', 'toasts.enrollment_approve_failed');
    },
  });
}

export function usePendingEnrollments(schoolId: string) {
  return useQuery<PendingEnrollmentResponse[]>({
    queryKey: ['pending-enrollments', schoolId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/enrollments/pending`);
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useGetScheduleEnrollmentsBySchedule(schoolId: string, scheduleId: string) {
  return useQuery<ScheduleEnrollmentResponse[]>({
    queryKey: ['enrollments', schoolId, 'groups', 'schedule', scheduleId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/enrollments/groups/schedule/${scheduleId}`);
      return res.data;
    },
    enabled: !!schoolId && !!scheduleId,
  });
}

export function useGetScheduleEnrollmentsByStudent(schoolId: string, studentMemberId: string) {
  return useQuery<ScheduleEnrollmentResponse[]>({
    queryKey: ['enrollments', schoolId, 'groups', 'student', studentMemberId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/enrollments/groups/student/${studentMemberId}`);
      return res.data;
    },
    enabled: !!schoolId && !!studentMemberId,
  });
}

export function useEnrollStudentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ schoolId, data }: { schoolId: string; data: EnrollStudentGroupRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/groups`, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      // Group enrollment auto-approves the student's pending session requests — refresh the dashboard widget.
      qc.invalidateQueries({ queryKey: ['pending-enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['members', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['coupons', variables.schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/coupons`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
      toast.success('Zawodnik został zapisany do grupy');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się zapisać do grupy', code, args);
    },
  });
}

export function useTransferStudentGroupNextMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ schoolId, data }: { schoolId: string; data: TransferStudentGroupNextMonthRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/groups/transfer-next-month`, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['members', variables.schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
      toast.success('Zawodnik został przeniesiony do nowej grupy od kolejnego miesiąca');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się przenieść zawodnika', code, args);
    },
  });
}

// ---- Waitlists ----

export function useJoinWaitlist(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post(`/schools/${schoolId}/waitlist`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['my-waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Successfully joined waitlist');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to join waitlist', code, args);
    },
  });
}

/** Query options of `useListMyWaitlists` — shared with the offline prefetch. */
export function myWaitlistsQueryOptions(schoolId: string, params?: { page?: number; pageSize?: number }) {
  return queryOptions({
    queryKey: ['my-waitlists', schoolId, params],
    queryFn: async (): Promise<any> => {
      const res = await api.get(`/schools/${schoolId}/waitlist/student/me`, { params });
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useListMyWaitlists(schoolId: string, params?: { page?: number; pageSize?: number }) {
  return useQuery(myWaitlistsQueryOptions(schoolId, params));
}

export function useCancelWaitlist(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/schools/${schoolId}/waitlist/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['waitlists', schoolId] });
      toast.success('Waitlist entry cancelled');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Failed to cancel waitlist entry');
    },
  });
}

export function useQueryWaitlists(schoolId: string, params?: { sessionId?: string; scheduleId?: string; studentMemberId?: string; status?: string; page?: number; pageSize?: number }) {
  return useQuery<any>({
    queryKey: ['waitlists', schoolId, params],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/waitlist`, { params });
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function usePromoteWaitlistEntry(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: string | { id: string; allowOverbooking?: boolean }) => {
      const id = typeof args === 'string' ? args : args.id;
      const overbooking = typeof args === 'object' ? !!args.allowOverbooking : false;
      await api.post(`/schools/${schoolId}/waitlist/${id}/promote${overbooking ? '?allowOverbooking=true' : ''}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['payments', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Successfully promoted student');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const code = data?.code || data?.Code;
      if (code === 'group_full_overbooking_required' || code === 'session_full_overbooking_required') {
        return;
      }
      const detail = data?.detail || data?.Detail;
      // Pass the business-rule code so the toast shows the translated message (e.g. schedule conflict)
      toast.error(detail || 'Failed to promote waitlist entry', code, data?.args || data?.Args);
    },
  });
}

export function useClaimWaitlistEntry(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/schools/${schoolId}/waitlist/${id}/claim`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['my-waitlists', schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['payments', schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Failed to claim waitlist spot');
    },
  });
}

// ---- Subscription Suspensions ----

export function useGetStudentSuspensions(schoolId: string, studentMemberId?: string) {
  return useQuery<SubscriptionSuspensionResponse[]>({
    queryKey: ['suspensions', schoolId, 'student', studentMemberId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/suspensions/student/${studentMemberId}`);
      return res.data;
    },
    enabled: !!schoolId && !!studentMemberId,
  });
}

export function useGetScheduleSuspensions(schoolId: string, scheduleId?: string) {
  return useQuery<SubscriptionSuspensionResponse[]>({
    queryKey: ['suspensions', schoolId, 'schedule', scheduleId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/suspensions/schedule/${scheduleId}`);
      return res.data;
    },
    enabled: !!schoolId && !!scheduleId,
  });
}

export function useCreateSubscriptionSuspension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      schoolId,
      studentMemberId,
      scheduleId,
      data,
    }: {
      schoolId: string;
      studentMemberId: string;
      scheduleId: string;
      data: CreateSubscriptionSuspensionRequest;
    }) => {
      const res = await api.post(
        `/schools/${schoolId}/suspensions/students/${studentMemberId}/schedules/${scheduleId}`,
        data
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['suspensions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Nie udało się zawiesić abonamentu');
    },
  });
}

export function useCancelSubscriptionSuspension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      schoolId,
      suspensionId,
    }: {
      schoolId: string;
      suspensionId: string;
    }) => {
      await api.delete(`/schools/${schoolId}/suspensions/${suspensionId}`);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['suspensions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Nie udało się wznowić abonamentu');
    },
  });
}

export function useUpdateSubscriptionSuspension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      schoolId,
      suspensionId,
      data,
    }: {
      schoolId: string;
      suspensionId: string;
      data: UpdateSubscriptionSuspensionRequest;
    }) => {
      const res = await api.put(
        `/schools/${schoolId}/suspensions/${suspensionId}`,
        data
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['suspensions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Nie udało się zaktualizować zawieszenia');
    },
  });
}

export function useUpdateSubscriptionEndDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      schoolId,
      data,
    }: {
      schoolId: string;
      data: UpdateSubscriptionEndDateRequest;
    }) => {
      const res = await api.post(
        `/schools/${schoolId}/enrollments/groups/end-date`,
        data
      );
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['suspensions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['scheduleEnrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['student-subscriptions'] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${variables.schoolId}/me`] });
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      toast.error(detail || 'Nie udało się zaktualizować daty zakończenia subskrypcji');
    },
  });
}
